import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { ActionGetAgentStepSensitiveDataResponseModel } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { Label } from 'src/app/labels/englishLabels';
import { HttpErrorResponse } from '@angular/common/http';
import { prettyMessageAsStringAsync, SupportedFormats } from 'src/app/common/prettyUtils';
import { Observable } from 'rxjs/internal/Observable';
import { CopyButtonComponent, PopupPosition } from '../copy-button/copy-button.component';
import { BehaviorSubject, EMPTY, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { StripHtmlPipe } from 'src/app/pipes/strip-html/strip-html.pipe';

@Component({
  selector: 'app-get-sensitive-data-input-action',
  standalone: true,
  templateUrl: './get-sensitive-data-input.component.html',
  styleUrls: ['./get-sensitive-data-input.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatSlideToggleModule,
    CopyButtonComponent,
    LabelsProviderPipe,
    MatTooltipModule,
    MatProgressSpinnerModule,
    StripHtmlPipe,
  ],
})
export class GetSensitiveDataActionComponent implements OnInit {
  @Input()
  public sensitiveDataMethod?: () => Observable<ActionGetAgentStepSensitiveDataResponseModel>;
  @Input() public showPopupBtn = true;
  @Input() public content?: string;
  @Input() public sensitiveDataIndexes?: string;

  public sensitiveData = '';
  public useInnerHtml = false;
  public showRawDataLabel = '';
  public rawSensitiveDataMode = false;
  public showRawDataSwitch = false;
  public popUpPosition = PopupPosition.Above;
  public sortedSensitivePositions: number[] = [];
  private readonly _sensitiveBlockSubject = new BehaviorSubject<boolean>(false);
  public isSensitiveBlockVisible$ = this._sensitiveBlockSubject.asObservable();
  private readonly _spinnerSubject = new BehaviorSubject<boolean>(false);
  public isSpinnerVisible$ = this._spinnerSubject.asObservable();
  public sensitiveIconToolTipLabelPath = 'aiAgentStepTranscriptDialog.sensitiveIconToolTip';

  constructor(
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService,
    private readonly _destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    if (!this.showPopupBtn && this.content) {
      this._sensitiveBlockSubject.next(true);
      prettyMessageAsStringAsync(this.content)
        .then((formattedTranscript) => {
          this.sensitiveData = formattedTranscript.text;
          this.useInnerHtml = this.showRawDataSwitch =
            this._isFormatSupportForInnerHtml(formattedTranscript.format) ||
            formattedTranscript.text.toLowerCase().includes('<html>') ||
            formattedTranscript.text.toLowerCase().includes('<div>');
        })
        .catch(() => {
          this.sensitiveData = this.content ? this.content : ''; // Fallback to the original transcript
        });
    }
    this.showRawDataLabel = Label.get('aiAgentStepTranscriptDialog.showRawData');
  }

  public getSensitiveData(event: MouseEvent): void {
    event.stopPropagation();
    of(null)
      .pipe(
        tap(() => {
          this._sensitiveBlockSubject.next(true);
          this._spinnerSubject.next(true);
        }),
        switchMap(() => {
          if (!this.sensitiveDataMethod) {
            console.error('sensitiveDataMethod is undefined!');
            return EMPTY;
          }
          return this.sensitiveDataMethod();
        }),
        tap(async (response: ActionGetAgentStepSensitiveDataResponseModel) => {
          let sensitiveData = '';
          if (response.transcript) {
            sensitiveData = response.transcript;
          } else if (response.payload) {
            sensitiveData = response.payload;
          } else if (response.invocationResult) {
            sensitiveData = Object.entries(response.invocationResult)
              .map(([key, value]) => `${key}, value: ${value}`)
              .join('\n');
          } else if (response.invocationParams) {
            if (response.invocationParams && Object.keys(response.invocationParams).length > 0) {
              sensitiveData = Object.entries(response.invocationParams)
                .map(([, value]) => {
                  if (value && Object.keys(value).length > 0) {
                    return Object.entries(value)
                      .map(([key, val]) => `${key}: ${val}`)
                      .join('\n');
                  }
                  return '';
                })
                .join();
            }
          } else if (response.singleSnippet) {
            sensitiveData = response.singleSnippet;
          }

          if (this.sensitiveDataIndexes && sensitiveData) {
            const indexes = this.sensitiveDataIndexes.split(',').map((str) => parseInt(str.trim(), 10));
            this.sortedSensitivePositions = [...indexes]
              .filter((x) => x >= 0 && x < sensitiveData.length)
              .sort((a, b) => a - b);
          }

          await prettyMessageAsStringAsync(sensitiveData)
            .then((formattedTranscript) => {
              this.sensitiveData = formattedTranscript.text;
              this.useInnerHtml = this.showRawDataSwitch = this._isFormatSupportForInnerHtml(
                formattedTranscript.format
              );
            })
            .catch(() => {
              this.sensitiveData = sensitiveData; // Fallback to the original transcript
            });
          this.showRawDataLabel = Label.get('aiAgentStepTranscriptDialog.showRawData');
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        error: (httpError: HttpErrorResponse) => {
          if (this._httpErrorHandlerService.isUnhandledExceptionError(httpError.status)) {
            this._httpErrorHandlerService.handle(httpError);
          } else {
            this._notificationService.error(httpError.error.detail);
          }
          this._sensitiveBlockSubject.next(false);
          this._spinnerSubject.next(false);
        },
        complete: () => {
          this._spinnerSubject.next(false);
        },
      });
  }

  private _isFormatSupportForInnerHtml(format: SupportedFormats): boolean {
    return format === SupportedFormats.HTML || format === SupportedFormats.XML;
  }
}
